import { zodResolver } from "@hookform/resolvers/zod";
import GoogleIcon from "@mui/icons-material/Google";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Checkbox,
    Container,
    Divider,
    FormControlLabel,
    FormHelperText,
    LinearProgress,
    Paper,
    Rating,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { RhfCheckbox, RhfTextField } from "mui-rhf-integration";
import { type ReactNode, useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod/mini";
import { useCreateReviewMutation } from "@/mutations/review.js";
import { useQueryOptionsFactory } from "@/queries";
import { defaultMutationErrorHandler } from "@/utils/api.ts";

const schema = z.object({
    rating: z.number().check(z.gte(1), z.lte(5)),
    comment: z.string().check(z.trim()),
    wouldRecommend: z.boolean(),
    publicSharingAllowed: z.boolean(),
    referralOptIn: z.boolean(),
});

type ReviewFieldValues = z.input<typeof schema>;
type ReviewTransformedValues = z.output<typeof schema>;

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { requestId } = Route.useParams();
    const request = useSuspenseQuery(qof.reviewRequest.get(requestId)).data;

    const form = useForm<ReviewFieldValues, unknown, ReviewTransformedValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            comment: "",
            wouldRecommend: false,
            referralOptIn: false,
        },
    });

    const createMutation = useCreateReviewMutation();
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = useCallback(
        (values: ReviewTransformedValues) => {
            createMutation.mutate(
                { requestId, ...values },
                {
                    onSuccess: () => {
                        setSubmitted(true);
                    },
                    onError: defaultMutationErrorHandler,
                },
            );
        },
        [createMutation, requestId],
    );

    let content: ReactNode;

    if (submitted) {
        content = (
            <Alert severity="success" sx={{ mt: 2 }}>
                Thank you, we have received your review. You can now close this page.
            </Alert>
        );
    } else if (request.reviewSubmittedAt !== null) {
        content = (
            <Alert severity="error" sx={{ mt: 2 }}>
                You have already written a review.
            </Alert>
        );
    } else {
        content = (
            <Paper
                component="form"
                onSubmit={form.handleSubmit(handleSubmit)}
                noValidate
                sx={{ p: 4 }}
            >
                <Stack spacing={2} direction={{ xs: "column", md: "row" }} sx={{ mb: 2 }}>
                    <TextField
                        label="Name"
                        value={request.fullName}
                        disabled
                        sx={{ width: { xs: "100%", md: "50%" } }}
                    />
                    <TextField
                        label="Email Address"
                        value={request.emailAddress}
                        disabled
                        sx={{ width: { xs: "100%", md: "50%" } }}
                    />
                </Stack>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">Overall Rating</Typography>
                    <Controller
                        control={form.control}
                        name="rating"
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <>
                                <Rating
                                    size="large"
                                    value={value ?? null}
                                    onChange={(_event, value) => onChange(value)}
                                />

                                {error && <FormHelperText error>{error.message}</FormHelperText>}
                            </>
                        )}
                    />
                </Box>

                <RhfTextField
                    control={form.control}
                    name="comment"
                    label="Tell us about your experience"
                    multiline
                    minRows={4}
                    fullWidth
                    sx={{ mb: 2 }}
                />

                <FormControlLabel
                    control={<RhfCheckbox control={form.control} name="wouldRecommend" />}
                    label="I would recommend Wingert Land Services to others"
                />

                <Card sx={{ backgroundColor: "background.default", mt: 2, mb: 3 }}>
                    <CardHeader
                        avatar={<GoogleIcon color="info" />}
                        title="Google Review (Optional)"
                        slotProps={{
                            title: {
                                variant: "h6",
                            },
                        }}
                    />
                    <CardContent sx={{ py: 0 }}>
                        <Typography variant="body2">
                            Help others find us by leaving a review on Google. This is completely
                            optional.
                        </Typography>
                    </CardContent>
                    <CardActions>
                        <Button
                            color="info"
                            component="a"
                            href="https://www.google.com/maps/place/Wingert+Land+Services/@44.1406169,-93.9627278,17z/data=!4m8!3m7!1s0x87f439edde0b22e7:0x60570fc73569dc6a!8m2!3d44.1406169!4d-93.9601475!9m1!1b1!16s%2Fg%2F1tgfx5jt?entry=ttu&g_ep=EgoyMDI1MDkwNy4wIKXMDSoASAFQAw%3D%3D#:~:text=4%20reviews-,%EE%95%A0,-Write%20a%20review"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Leave a Google Review
                        </Button>
                    </CardActions>
                </Card>

                <Typography variant="h6">Sharing Preferences</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Help us share your positive experience with potential clients.
                </Typography>

                <Controller
                    control={form.control}
                    name="publicSharingAllowed"
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <>
                            <FormControlLabel
                                control={<Checkbox checked={value === true} />}
                                onChange={(_event, checked) => {
                                    if (checked) {
                                        onChange(true);
                                    }
                                }}
                                label="Allow my full review to be shared with public (website, marketing materials, social, etc.).  Your review will be identified with your first initial, last name, and county your property was located in."
                            />
                            <FormControlLabel
                                control={<Checkbox checked={value === false} />}
                                onChange={(_event, checked) => {
                                    if (checked) {
                                        onChange(false);
                                    }
                                }}
                                label="Do not allow my full review to be shared with public, only with Wingert Land Services team."
                            />
                            {error && <FormHelperText error>{error.message}</FormHelperText>}
                        </>
                    )}
                />

                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                    control={<RhfCheckbox control={form.control} name="referralOptIn" />}
                    label="I am willing to be a referral source for future Wingert Land Services clients.  I approve of my contact information being shared upon request of future Wingert Land Services clients."
                />

                <Typography variant="body2" sx={{ mt: 1 }}>
                    Your name and contact information will never be shared without your explicit
                    permission.
                </Typography>

                <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Button type="submit" variant="contained" size="large">
                        Submit Review
                    </Button>
                </Box>
            </Paper>
        );
    }

    return (
        <Container sx={{ py: 2 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ mb: 1 }}>
                    How Was Your Experience?
                </Typography>
                <Typography>
                    Please take a moment to share your feedback about our services.
                </Typography>
            </Box>

            {content}
        </Container>
    );
};

export const Route = createFileRoute("/review/$requestId")({
    component: Root,
    pendingComponent: () => <LinearProgress />,
    loader: async ({ context, params }) => {
        await context.queryClient.ensureQueryData(context.qof.reviewRequest.get(params.requestId));
    },
});
